import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Users, Star, ArrowLeft } from 'lucide-react';
import { useSurveyTemplates, useSurveyTemplate } from '@/hooks/useSurveyTemplates';
import { SurveyTemplateViewer } from './SurveyTemplateViewer';

interface SurveyTemplateCatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUseTemplate?: (templateId: string) => void;
}

export const SurveyTemplateCatalogDialog = ({ 
  open, 
  onOpenChange, 
  onUseTemplate 
}: SurveyTemplateCatalogDialogProps) => {
  const navigate = useNavigate();
  const { toolId, lang } = useParams<{ toolId: string; lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  
  // Fetch ALL templates (not filtered by tool)
  const { data: templates, isLoading, error } = useSurveyTemplates();

  const handleBack = () => {
    setSelectedTemplateId(null);
  };

  const handleUseTemplate = (templateId: string) => {
    onUseTemplate?.(templateId);
    onOpenChange(false);
    setSelectedTemplateId(null);
  };

  const renderTemplateList = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="h-48">
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-1/2 mb-2" />
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Failed to load survey templates</p>
        </div>
      );
    }

    if (!templates || templates.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No survey templates available</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {template.name}
                    {template.is_featured && (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {template.description}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {template.estimated_duration_minutes && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {template.estimated_duration_minutes}min
                  </div>
                )}
                {template.target_audience && (
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {template.target_audience}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="capitalize">
                  {template.category}
                </Badge>
                <Button 
                  size="sm" 
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`${prefix}/tools/${toolId}/results?surveyTemplateId=${template.id}`);
                  }}
                  className="bg-gradient-primary text-white"
                >
                  Use template
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {selectedTemplateId && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="p-1"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle>
              {selectedTemplateId ? 'Template Details' : 'Survey Template Catalog'}
            </DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="overflow-y-auto">
          {selectedTemplateId ? (
            <SurveyTemplateViewer
              templateId={selectedTemplateId}
              onBack={handleBack}
              onUseTemplate={handleUseTemplate}
            />
          ) : (
            renderTemplateList()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};